<script setup lang="ts">
import { useChat } from '@/composables/useChat'

const { sources } = useChat()

const PROVIDER_LABELS: Record<string, string> = {
  azure_search: 'Azure Search',
  web_search: 'Web',
  perplexity: 'Perplexity',
  search_perplexity: 'Perplexity',
  mcp: 'MCP',
}

function providerLabel(p: string): string {
  return PROVIDER_LABELS[p] || p
}

function providerColor(p: string): string {
  switch (p) {
    case 'azure_search': return 'bg-blue-50 text-blue-700 border-blue-200'
    case 'perplexity':
    case 'search_perplexity': return 'bg-violet-50 text-violet-700 border-violet-200'
    case 'web_search': return 'bg-amber-50 text-amber-700 border-amber-200'
    case 'mcp': return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    default: return 'bg-neutral-100 text-neutral-600 border-neutral-200'
  }
}
</script>

<template>
  <aside class="flex flex-col bg-white border-l border-neutral-200 overflow-hidden">
    <div class="flex items-center justify-between px-3 py-2.5 min-h-[48px] border-b border-neutral-200 shrink-0">
      <h2 class="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">
        Sources
      </h2>
      <span class="text-[11px] text-neutral-400">{{ sources.length }}</span>
    </div>

    <div class="flex-1 overflow-y-auto p-3 space-y-2">
      <div
        v-if="sources.length === 0"
        class="text-center text-xs text-neutral-400 py-6"
      >
        No sources retrieved yet.
      </div>
      <article
        v-for="(s, idx) in sources"
        :key="s.id || s.url || idx"
        class="border border-neutral-200 rounded-md p-2.5 hover:border-blue-300 transition-colors"
      >
        <div class="flex items-center gap-2 mb-1">
          <span
            class="text-[10px] uppercase px-1.5 py-0.5 rounded border"
            :class="providerColor(s.provider)"
          >
            {{ providerLabel(s.provider) }}
          </span>
          <span v-if="s.score != null" class="text-[10px] text-neutral-400">
            score {{ s.score.toFixed(2) }}
          </span>
        </div>
        <div class="text-[13px] font-medium text-neutral-800 leading-tight">
          <a
            v-if="s.url"
            :href="s.url"
            target="_blank"
            rel="noopener noreferrer"
            class="hover:text-blue-600 hover:underline"
          >
            {{ s.title || s.url }}
          </a>
          <span v-else>{{ s.title || 'Untitled source' }}</span>
        </div>
        <p
          v-if="s.snippet"
          class="text-[12px] text-neutral-500 mt-1.5 leading-snug line-clamp-3"
        >
          {{ s.snippet }}
        </p>
      </article>
    </div>
  </aside>
</template>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
